import { useCallback, useEffect, useState } from "react";

/** Travel album: one photo per visited stop, stored only on the device (IndexedDB), never uploaded. */
const DB = "atlas-album";
const STORE = "photos";

function open() {
  return new Promise((res, rej) => {
    const r = indexedDB.open(DB, 1);
    r.onupgradeneeded = () => r.result.createObjectStore(STORE);
    r.onsuccess = () => res(r.result); r.onerror = () => rej(r.error);
  });
}
const tx = async (mode, fn) => { const db = await open(); return new Promise((res, rej) => { const t = db.transaction(STORE, mode); const req = fn(t.objectStore(STORE)); t.oncomplete = () => res(req?.result); t.onerror = () => rej(t.error); }); };
const key = (code, stop) => `${code}::${stop}`;

export const albumPut = (code, stop, blob) => tx("readwrite", (s) => s.put(blob, key(code, stop)));
export const albumDel = (code, stop) => tx("readwrite", (s) => s.delete(key(code, stop)));
export const albumClear = () => tx("readwrite", (s) => s.clear());
export async function albumList(code) {
  const db = await open();
  return new Promise((res, rej) => {
    const out = {}; const range = IDBKeyRange.bound(`${code}::`, `${code}::\uffff`);
    const req = db.transaction(STORE).objectStore(STORE).openCursor(range);
    req.onsuccess = () => { const c = req.result; if (!c) return res(out); out[c.key.split("::")[1]] = c.value; c.continue(); };
    req.onerror = () => rej(req.error);
  });
}

/** Downscale a camera photo to ≤1280px JPEG before storing. */
export function shrink(file, max = 1280) {
  return new Promise((res) => {
    const img = new Image(); const url = URL.createObjectURL(file);
    img.onload = () => {
      const k = Math.min(1, max / Math.max(img.width, img.height));
      const c = document.createElement("canvas"); c.width = Math.round(img.width * k); c.height = Math.round(img.height * k);
      c.getContext("2d").drawImage(img, 0, 0, c.width, c.height); URL.revokeObjectURL(url);
      c.toBlob((b) => res(b), "image/jpeg", 0.85);
    };
    img.src = url;
  });
}

export function useAlbum(code) {
  const [photos, setPhotos] = useState({});
  const reload = useCallback(() => { if (code && "indexedDB" in window) albumList(code).then(setPhotos).catch(() => setPhotos({})); }, [code]);
  useEffect(reload, [reload]);
  const add = useCallback(async (stop, file) => { await albumPut(code, stop, await shrink(file)); reload(); }, [code, reload]);
  const remove = useCallback(async (stop) => { await albumDel(code, stop); reload(); }, [code, reload]);
  return { photos, add, remove };
}

const loadImg = (src) => new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; });
const cover = (ctx, img, x, y, w, h) => { const k = Math.max(w / img.width, h / img.height); const sw = w / k, sh = h / k; ctx.drawImage(img, (img.width - sw) / 2, (img.height - sh) / 2, sw, sh, x, y, w, h); };

/** 1080×1350 memory postcard: up to 4 photos in a mosaic + flag, title and footer. Pure client side. */
export async function renderMemoryCard({ blobs, flagUrl, title, subtitle, rtl }) {
  const W = 1080, H = 1350; const c = document.createElement("canvas"); c.width = W; c.height = H; const ctx = c.getContext("2d");
  ctx.fillStyle = "#F4F1EA"; ctx.fillRect(0, 0, W, H);
  const imgs = await Promise.all(blobs.slice(0, 4).map((b) => loadImg(URL.createObjectURL(b))));
  const slots = { 1: [[40, 40, 1000, 1000]], 2: [[40, 40, 490, 1000], [550, 40, 490, 1000]], 3: [[40, 40, 1000, 490], [40, 550, 490, 490], [550, 550, 490, 490]], 4: [[40, 40, 490, 490], [550, 40, 490, 490], [40, 550, 490, 490], [550, 550, 490, 490]] }[imgs.length] || [];
  imgs.forEach((im, i) => { const [x, y, w, h] = slots[i]; ctx.save(); ctx.beginPath(); ctx.roundRect(x, y, w, h, 28); ctx.clip(); cover(ctx, im, x, y, w, h); ctx.restore(); });
  ctx.fillStyle = "#2B2726"; ctx.fillRect(0, 1080, W, 270);
  try { const f = await loadImg(flagUrl); ctx.save(); ctx.beginPath(); ctx.roundRect(60, 1125, 150, 100, 12); ctx.clip(); cover(ctx, f, 60, 1125, 150, 100); ctx.restore(); } catch { /* flag optional */ }
  ctx.direction = rtl ? "rtl" : "ltr"; ctx.textAlign = rtl ? "right" : "left"; const tx0 = rtl ? W - 60 : 240;
  ctx.fillStyle = "#FFFFFF"; ctx.font = "bold 60px 'Playfair Display', 'Noto Naskh Arabic', serif"; ctx.fillText(title, tx0, 1185, rtl ? 740 : 780);
  ctx.fillStyle = "#C86641"; ctx.font = "500 32px 'Inter', 'Noto Naskh Arabic', sans-serif"; ctx.fillText(subtitle, tx0, 1240, 780);
  ctx.fillStyle = "#EBDDC2"; ctx.font = "bold 26px sans-serif"; ctx.textAlign = rtl ? "left" : "right"; ctx.fillText("mondearabe.org", rtl ? 60 : W - 60, 1310);
  return new Promise((res) => c.toBlob(res, "image/png"));
}
